import { Button } from '@octopus-do/octopus-storybook'
import type { PublishData } from '@storeOld/reducers/publish'
import type React from 'react'

import PlayIcon from '@/assets/images/icons/play-icon.svg?react'
import UpdateIcon from '@/assets/images/icons/update-icon.svg?react'
import { COLORS_HEX } from '@/constants/colors'
import type { ModalProps } from '@/types'
import { clsJoin } from '@/utils'
import { ModalHeader } from '../components/ModalHeader'
import {
  BackgroundPicker,
  Preview,
  PreviewSwitcher,
  PublishTutorial,
} from './components'
import { CONTENTS, FONTS, PRESETS } from './constants'
import { getIcon } from './helper/icons'
import { usePublishModal } from './hooks'
import css from './styles.module.css'

type Props = {
  edit?: boolean
  onPublishUpdate: (publish: PublishData | null) => void
} & ModalProps

export const PublishModal: React.FC<Props> = ({
  edit,
  onPublishUpdate,
  onClose,
}) => {
  const {
    bgWhite,
    activeBgColor,
    themeClassName,
    activeContent,
    pagePreview,
    activeFont,
    hasHeaderShadow,
    bodyRef,
    activePreset,
    hasFooterShadow,
    isLoading,
    handleScroll,
    handleSetPreset,
    handleSetFont,
    handleSetContent,
    handleSetBgColor,
    handleUpdatePublish,
    handlePublish,
    handlePublishPagePreview,
  } = usePublishModal(onPublishUpdate)

  const backgroundColor = bgWhite ? 'var(--color-code-bw-30)' : activeBgColor

  return (
    <div className={css.wrapper}>
      <div
        className={clsJoin(css.preview_wrap, themeClassName)}
        style={{ backgroundColor }}
      >
        <div className={clsJoin(css.overlay, bgWhite && css.bg_white)} />
        <Preview
          bgColor={activeBgColor}
          contentType={activeContent}
          pagePreview={pagePreview}
          fontType={activeFont}
        />
      </div>
      <div className={css.settings}>
        <ModalHeader
          title="Publish"
          showShadow={hasHeaderShadow}
          onClose={onClose}
        />

        <div ref={bodyRef} className={css.body} onScroll={handleScroll}>
          <div className={css.group}>
            <h3 className={css.group_title}>Presets</h3>

            <div className={css.group_grid}>
              {Object.values(PRESETS).map((preset) => (
                <div
                  key={preset.name}
                  className={clsJoin(
                    css.group_item,
                    preset.name === activePreset?.name && css.active,
                    preset.bgColor === COLORS_HEX.white && css.with_border
                  )}
                  style={{
                    backgroundColor: preset.bgColor,
                  }}
                  onClick={() => handleSetPreset(preset)}
                >
                  {getIcon(preset.name)}
                </div>
              ))}
            </div>
          </div>

          <div className={css.divider} />

          <div className={css.group}>
            <h3 className={css.group_title}>Font</h3>

            <div className={css.group_grid}>
              {FONTS.map((font) => (
                <div
                  key={font}
                  className={clsJoin(
                    css.group_item,
                    css.with_border,
                    font === activeFont && css.active
                  )}
                  onClick={() => handleSetFont(font)}
                >
                  <span className={css.item_name}>{font}</span>

                  <span className={css.font_img}>{getIcon(font)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className={css.divider} />

          <div className={css.group}>
            <h3 className={css.group_title}>Content</h3>

            <div className={css.group_grid}>
              {CONTENTS.map((content) => (
                <div
                  key={content}
                  className={clsJoin(
                    css.group_item,
                    css.with_bg,
                    content === activeContent && css.active
                  )}
                  onClick={() => handleSetContent(content)}
                >
                  <span className={css.item_name}>{content}</span>
                  <span className={css.content_img}>{getIcon(content)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className={css.divider} />

          <BackgroundPicker
            hexCode={activeBgColor}
            onSetColor={handleSetBgColor}
          />

          <div className={css.divider} />

          <PreviewSwitcher
            checked={pagePreview}
            onChange={handlePublishPagePreview}
          />
        </div>

        <div
          className={clsJoin(
            css.footer,
            !hasFooterShadow && css.without_shadow
          )}
        >
          {edit ? (
            <Button
              className={css.wide_button}
              size="medium"
              label="Update"
              left={UpdateIcon}
              loading={isLoading}
              onClick={handleUpdatePublish}
            />
          ) : (
            <Button
              className={css.wide_button}
              size="medium"
              label="Publish"
              left={PlayIcon}
              loading={isLoading}
              onClick={handlePublish}
            />
          )}
        </div>
      </div>

      <PublishTutorial />
    </div>
  )
}
